// src/components/RecepcionDigital/RecepcionDigital.jsx
//
// ── Design decisions ──────────────────────────────────────────────────────────
// Module shell for Recepción Digital: two tabs (Manifiesto, Producción), a
// global Ctrl+K command palette, and the navigation state between both tabs.
//
// - The shell owns NO data. Each tab fetches its own drafts through the mock
//   API layer so the tabs stay independently testable and the future backend
//   swap touches only src/api. (Principio: superficie de triaje vs. ensamblaje.)
// - Producción without an encomienda seleccionada renders a warm empty state
//   instead of a blank surface (Principio 5: los estados vacíos tienen calidez).
// - Ctrl+K is registered at the shell level so the palette opens from either
//   tab (Principio 9: atajos de teclado en todo el módulo). Selecting a result
//   jumps straight into Producción — phone → encomienda in under 2 seconds.
// - Tabs are buttons with aria-current, mirroring the app's .nav-tab pattern,
//   desktop-first at 1440px (Principio 1).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react';
import { Inbox } from 'lucide-react';
import Manifiesto from './Manifiesto';
import Produccion from './Produccion';
import CommandPalette from './shared/CommandPalette';
import EmptyState from './shared/EmptyState';

const RecepcionDigital = ({ user }) => {
  const [activeTab, setActiveTab] = useState('manifiesto'); // 'manifiesto' | 'produccion'
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ── Global Ctrl+K — abre el buscador desde cualquier pestaña ───────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const openInProduccion = useCallback((draftId) => {
    setSelectedDraftId(draftId);
    setActiveTab('produccion');
  }, []);

  const backToManifiesto = useCallback(() => {
    setSelectedDraftId(null);
    setActiveTab('manifiesto');
  }, []);

  const tabButton = (key, label) => (
    <button
      key={key}
      onClick={() => setActiveTab(key)}
      aria-current={activeTab === key ? 'page' : undefined}
      aria-label={`Pestaña ${label}`}
      className="min-h-[44px] px-4 text-sm font-medium border-b-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
      style={{
        borderBottomColor: activeTab === key ? 'var(--avianca-red)' : 'transparent',
        color: activeTab === key ? 'var(--text-main)' : 'var(--text-secondary)',
        outlineColor: 'var(--avianca-red)',
        background: 'transparent',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="w-full max-w-[1440px] mx-auto px-6 pb-10" style={{ color: 'var(--text-main)' }}>
      {/* ── Cabecera del módulo ── */}
      <div
        className="flex items-center justify-between sticky top-0 z-10 pt-4"
        style={{ background: 'var(--bg)' }}
      >
        <div className="flex items-end gap-2" role="tablist" aria-label="Pestañas de Recepción Digital">
          {tabButton('manifiesto', 'Manifiesto')}
          {tabButton('produccion', 'Producción')}
        </div>
        <button
          onClick={() => setPaletteOpen(true)}
          aria-label="Abrir buscador de encomiendas (Ctrl+K)"
          title="Buscar encomienda por teléfono o nombre (Ctrl+K)"
          className="min-h-[44px] flex items-center gap-2 px-3 rounded-lg border text-sm transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
          style={{
            background: 'var(--input-bg)',
            borderColor: 'var(--border)',
            color: 'var(--text-secondary)',
            outlineColor: 'var(--avianca-red)',
          }}
        >
          <span>Buscar encomienda…</span>
          <kbd
            className="text-[10px] px-1.5 py-0.5 rounded border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-tertiary)' }}
          >
            Ctrl+K
          </kbd>
        </button>
      </div>

      {/* ── Contenido de la pestaña ── */}
      <div className="mt-4">
        {activeTab === 'manifiesto' && (
          <Manifiesto user={user} onOpenDraft={openInProduccion} />
        )}
        {activeTab === 'produccion' && selectedDraftId && (
          <Produccion draftId={selectedDraftId} onBack={backToManifiesto} />
        )}
        {activeTab === 'produccion' && !selectedDraftId && (
          <div className="pt-16">
            <EmptyState
              icon={Inbox}
              title="Ninguna encomienda en producción"
              subtitle="Elija una tarjeta en el Manifiesto o busque por teléfono con Ctrl+K para empezar a ensamblar el Anexo."
            />
          </div>
        )}
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onSelect={(draftId) => {
          setPaletteOpen(false);
          openInProduccion(draftId);
        }}
      />
    </div>
  );
};

export default RecepcionDigital;
